import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SubscriptionHistory = ({ transactions, onDownloadInvoice, onViewDetails }) => {
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const periodOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_year', label: 'Last Year' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Transactions' },
    { value: 'subscription', label: 'Subscriptions' },
    { value: 'premium', label: 'Premium Features' },
    { value: 'refund', label: 'Refunds' }
  ];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-CM', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'pending': return 'text-warning';
      case 'failed': return 'text-error';
      case 'refunded': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'failed': return 'XCircle';
      case 'refunded': return 'RotateCcw';
      default: return 'Circle';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'subscription': return 'Globe';
      case 'premium': return 'Crown';
      case 'refund': return 'RotateCcw';
      default: return 'CreditCard';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'orange_money': return 'Smartphone';
      case 'mtn_momo': return 'Smartphone';
      case 'card': return 'CreditCard';
      case 'wallet': return 'Wallet';
      default: return 'CreditCard';
    }
  };

  const filteredTransactions = transactions?.filter(transaction => {
    if (filterType !== 'all' && transaction?.type !== filterType) return false;
    
    if (filterPeriod !== 'all') {
      const transactionDate = new Date(transaction.date);
      const now = new Date();
      const monthsBack = {
        'last_month': 1,
        'last_3_months': 3,
        'last_6_months': 6,
        'last_year': 12
      }?.[filterPeriod];
      
      if (monthsBack) {
        const cutoffDate = new Date(now.getFullYear(), now.getMonth() - monthsBack, now.getDate());
        if (transactionDate < cutoffDate) return false;
      }
    }
    
    return true;
  });

  const totalSpent = filteredTransactions?.filter(t => t?.status === 'completed' && t?.type !== 'refund')?.reduce((sum, t) => sum + t?.amount, 0);

  const totalRefunded = filteredTransactions?.filter(t => t?.status === 'completed' && t?.type === 'refund')?.reduce((sum, t) => sum + t?.amount, 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Subscription History</h2>
          <p className="text-muted-foreground">Track your payments and subscription activity</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
          <Select
            options={periodOptions}
            value={filterPeriod}
            onChange={setFilterPeriod}
            placeholder="Filter by period"
            className="w-full sm:w-40"
          />
          <Select
            options={typeOptions}
            value={filterType}
            onChange={setFilterType}
            placeholder="Filter by type"
            className="w-full sm:w-40"
          />
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">Total Spent</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatAmount(totalSpent)}</p>
          <p className="text-xs text-muted-foreground">{filteredTransactions?.filter(t => t?.status === 'completed' && t?.type !== 'refund')?.length} transactions</p>
        </div>

        <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm font-medium text-success">Successful</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {filteredTransactions?.filter(t => t?.status === 'completed')?.length}
          </p>
          <p className="text-xs text-muted-foreground">Completed payments</p>
        </div>

        {totalRefunded > 0 && (
          <div className="p-4 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="RotateCcw" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Refunded</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatAmount(totalRefunded)}</p>
            <p className="text-xs text-muted-foreground">{filteredTransactions?.filter(t => t?.type === 'refund')?.length} refunds</p>
          </div>
        )}
      </div>
      {/* Transactions List */}
      <div className="space-y-3">
        {filteredTransactions?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Receipt" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-foreground mb-2">No Transactions Found</h3>
            <p className="text-muted-foreground">No transactions match your current filters</p>
          </div>
        ) : (
          filteredTransactions?.map((transaction) => (
            <div
              key={transaction?.id}
              className="border border-border rounded-lg p-4 hover:shadow-soft transition-smooth"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                    <Icon name={getTypeIcon(transaction?.type)} size={20} className="text-primary" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-medium text-foreground">{transaction?.description}</h3>
                      <div className={`flex items-center space-x-1 ${getStatusColor(transaction?.status)}`}>
                        <Icon name={getStatusIcon(transaction?.status)} size={14} />
                        <span className="text-xs font-medium capitalize">{transaction?.status}</span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">{transaction?.details}</p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} />
                        <span>{formatDate(transaction?.date)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name={getPaymentMethodIcon(transaction?.paymentMethod)} size={12} />
                        <span>{transaction?.paymentMethodName}</span>
                      </div>
                      {transaction?.transactionId && (
                        <div className="flex items-center space-x-1">
                          <Icon name="Hash" size={12} />
                          <span>{transaction?.transactionId}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <p className={`text-lg font-semibold ${
                    transaction?.type === 'refund' ? 'text-success' : 'text-foreground'
                  }`}>
                    {transaction?.type === 'refund' ? '+' : ''}{formatAmount(transaction?.amount)}
                  </p>
                  
                  <div className="flex items-center space-x-2 mt-2">
                    {transaction?.invoiceUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDownloadInvoice(transaction?.id)}
                        iconName="Download"
                        iconSize={14}
                        title="Download Invoice"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(transaction?.id)}
                      iconName="Eye"
                      iconSize={14}
                      title="View Details"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info for Failed Transactions */}
              {transaction?.status === 'failed' && transaction?.failureReason && (
                <div className="mt-3 p-3 bg-error/5 border border-error/20 rounded">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-error">Payment Failed</p>
                      <p className="text-xs text-muted-foreground">{transaction?.failureReason}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Renewal Info */}
              {transaction?.type === 'subscription' && transaction?.renewalDate && (
                <div className="mt-3 p-3 bg-accent/5 border border-accent/20 rounded">
                  <div className="flex items-center space-x-2">
                    <Icon name="RotateCcw" size={14} className="text-accent" />
                    <p className="text-xs text-muted-foreground">
                      Next renewal: {formatDate(transaction?.renewalDate)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {/* Load More Button */}
      {filteredTransactions?.length > 0 && filteredTransactions?.length % 10 === 0 && (
        <div className="text-center mt-6">
          <Button
            variant="outline"
            iconName="ChevronDown"
            iconPosition="right"
            iconSize={16}
          >
            Load More Transactions
          </Button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionHistory;